import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'
import { getImage } from 'gatsby-plugin-image'
import OgImageFallback from '../img/og-img-logo-ganztags.png'

function Seo({ description, lang, meta, title, location, linksTo, noEnglish, image }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
          }
        }
      }
    `
  )

  const canonical = `https://www.ganztags.net${location.pathname}`;
  const canonicalTag = (<link rel="canonical" href={canonical} />);

  const metaDescription = description || site.siteMetadata.description

  const allAlternateDELanguageCodes = ['de-AT', 'de-CH', 'de-DE']
  const allAlternateENLanguageCodes = ['en-AU', 'en-CA', 'en-GB', 'en-NZ', 'en-US']

  const determinePath = (language = 'de') => {
    if (language === 'de') {
      if (!linksTo) return location.origin + location.pathname
      return location.origin + '/' + linksTo + '/'
    } else if (language === 'en') {
      if (!linksTo) return location.origin + '/en' + location.pathname
      return location.origin + '/en/' + linksTo + '/'
    }
  }

  const deAlternates =
    lang === 'de'
      ? allAlternateDELanguageCodes.map((alternate, idx) => (
          <link key={idx} rel="alternate" href={location.href} hrefLang={alternate} />
        ))
      : allAlternateDELanguageCodes.map((alternate, idx) => {
          return <link key={idx} rel="alternate" href={determinePath()} hrefLang={alternate} />
        })

  const enAlternates =
    lang === 'en'
      ? allAlternateENLanguageCodes.map((alternate, idx) => (
          <link key={idx} rel="alternate" href={location.href} hrefLang={alternate} />
        ))
      : allAlternateENLanguageCodes.map((alternate, idx) => {
          if (noEnglish) return ''
          return <link key={idx} rel="alternate" href={determinePath('en')} hrefLang={alternate} />
        })

  const defaultAlternate = noEnglish ? (
    <link rel="alternate" href={location.href} hrefLang="x-default" />
  ) : (
    <link rel="alternate" href={lang === 'en' ? location.href : determinePath('en')} hrefLang="x-default" />
  )

  // Get news article image or fallback to default image
  const ogImage = image ? getImage(image).images.fallback.src : OgImageFallback;

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:image`,
          content: ogImage,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: `ganztags. GmbH`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ].concat(meta)}
    >
      {deAlternates}
      {enAlternates}
      {defaultAlternate}
      {canonicalTag}
    </Helmet>
  )
}

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  noEnglish: false,
}

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default Seo
